import { useLayoutEffect, useRef } from 'react'
import { useI18n } from '@runtime/core/i18n/i18n'
import { primaryPlayNavigation } from './playNavigationDestinations'
import PlayNavigationItem from './PlayNavigationItem'
import styles from './PlayBottomNavigation.module.scss'

/** Compact responsive view of the existing global destinations and menu. */
export default function PlayBottomNavigation({
  onOpenMenu,
  menuOpen,
}: {
  onOpenMenu: (trigger: HTMLButtonElement) => void
  menuOpen: boolean
}) {
  const { t } = useI18n()
  const navigationRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const navigation = navigationRef.current
    const shell = navigation?.closest<HTMLElement>('[data-testid="play-shell"]')
    if (!navigation || !shell) return
    const property = '--play-bottom-navigation-height'
    const previous = shell.style.getPropertyValue(property)
    const measure = () =>
      shell.style.setProperty(property, `${navigation.getBoundingClientRect().height}px`)
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(navigation, { box: 'border-box' })
    window.addEventListener('resize', measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
      if (previous) shell.style.setProperty(property, previous)
      else shell.style.removeProperty(property)
    }
  }, [])
  const destinations = primaryPlayNavigation.filter(item =>
    ['home', 'strategy', 'puzzle', 'challenges'].includes(item.id),
  )
  return (
    <nav
      ref={navigationRef}
      className={styles.navigation}
      aria-label={t('common.playNavigation.label')}
      data-testid="play-bottom-navigation"
    >
      <ul>
        {destinations.map(item => (
          <PlayNavigationItem key={item.id} item={item} mobile />
        ))}
        <li>
          <button
            type="button"
            aria-expanded={menuOpen}
            aria-controls="play-mobile-menu"
            onClick={event => onOpenMenu(event.currentTarget)}
          >
            <span aria-hidden="true">{'\u2630'}</span>
            <span>{t('common.menu')}</span>
          </button>
        </li>
      </ul>
    </nav>
  )
}
